import { useEffect, useState } from 'react'
import { BoardIcon, FlowIcon, MessageIcon } from '../ProductFrameIcons'
import type { StudioMode } from './StudioShell'

export const MODES: readonly { id: StudioMode; label: string; body: string; Icon: typeof MessageIcon }[] = [
  { id: 'chat', label: 'Linear', body: 'Talk to the agent. Every result carries its own controls in the thread.', Icon: MessageIcon },
  { id: 'board', label: 'Artboard', body: 'Every result lands on an artboard you keep editing.', Icon: BoardIcon },
  { id: 'flow', label: 'Workflow', body: 'The same session as blocks you can rewire, automate and rerun.', Icon: FlowIcon },
]

const ROTATE_MS = 6000

/** Cycles the three workspace modes until the visitor picks one; then it stays put. */
export function useModeCycle(): [StudioMode, (m: StudioMode) => void] {
  const [mode, setMode] = useState<StudioMode>('board')
  const [pinned, setPinned] = useState(false)
  useEffect(() => {
    if (pinned || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const t = window.setInterval(() => {
      setMode((m) => MODES[(MODES.findIndex((x) => x.id === m) + 1) % MODES.length].id)
    }, ROTATE_MS)
    return () => window.clearInterval(t)
  }, [pinned])
  return [
    mode,
    (m) => {
      setPinned(true)
      setMode(m)
    },
  ]
}

/** The workspace switch in the app's shape: a segmented control, icon and label. */
export function ModeSwitcher({ mode, onPick }: { mode: StudioMode; onPick: (m: StudioMode) => void }) {
  const active = MODES.find((m) => m.id === mode) ?? MODES[1]
  return (
    <div className="flex flex-col items-center gap-3">
      <div role="tablist" aria-label="Workspace modes" className="flex items-center gap-1 rounded-xl bg-[#e4e2df] p-1">
        {MODES.map(({ id, label, Icon }) => {
          const on = id === mode
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => onPick(id)}
              className={`flex h-9 items-center gap-2 rounded-lg px-4 text-[14px] transition-colors duration-150 ${
                on ? 'bg-card text-ink shadow-[0_1px_3px_rgba(2,2,2,0.14)]' : 'text-ink-3 hover:text-ink'
              }`}
            >
              <Icon width={15} height={15} />
              {label}
            </button>
          )
        })}
      </div>
      <p className="text-[14px] text-ink-2" aria-live="polite">
        {active.body}
      </p>
    </div>
  )
}
